import React from 'react';
import { CalendarClock, AlertTriangle } from 'lucide-react';
import { PROSPECT_TYPES, actionItems, fmtDate, initials, money } from './crmConstants';

export const ActionBadges = ({ done = {}, size = 'sm', testIdPrefix }) => {
  const items = actionItems(done);
  if (!items.length) return <p className="text-[11px] text-slate-500 italic">Nessuna azione ancora</p>;
  const box = size === 'sm' ? 'w-6 h-6' : 'w-8 h-8';
  return (
    <div className="flex flex-wrap gap-1">
      {items.map(({ key, label, icon: Icon }) => (
        <span
          key={key}
          title={`${label} · ${fmtDate(done[key].at)}${done[key].by ? ` · ${done[key].by}` : ''}`}
          className={`${box} rounded-md flex items-center justify-center ${key === 'offer_accepted' ? 'bg-emerald-500/25 text-emerald-300' : key.startsWith('offer') ? 'bg-violet-500/20 text-violet-300' : 'bg-blue-500/15 text-blue-300'}`}
          data-testid={`${testIdPrefix}-badge-${key}`}
        >
          <Icon className="w-3.5 h-3.5" />
        </span>
      ))}
    </div>
  );
};

export const NextTaskChip = ({ lead }) => {
  if (!lead.next_task) return <p className="text-[11px] text-slate-500">Nessuna prossima azione</p>;
  const overdue = lead.overdue;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${overdue ? 'bg-rose-500/20 text-rose-300' : 'bg-slate-700 text-slate-200'}`} data-testid={`lead-next-task-${lead.id}`}>
      {overdue ? <AlertTriangle className="w-3 h-3" /> : <CalendarClock className="w-3 h-3" />}
      <span className="truncate max-w-[160px]">{lead.next_task.title}</span>
      {lead.next_task.due_at && <span className="opacity-70">· {fmtDate(lead.next_task.due_at)}</span>}
    </span>
  );
};

export const LeadCard = ({ lead, onOpen, draggable }) => (
  <div
    draggable={draggable}
    onDragStart={(e) => e.dataTransfer.setData('text/lead-id', lead.id)}
    onClick={() => onOpen(lead)}
    className={`group bg-slate-800/80 hover:bg-slate-800 border rounded-xl p-3 cursor-pointer transition-colors space-y-2 ${lead.overdue ? 'border-rose-500/50' : 'border-slate-700 hover:border-amber-500/50'}`}
    data-testid={`lead-card-${lead.id}`}
  >
    <div className="flex items-start gap-2">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white truncate">{lead.name || lead.email}</p>
        <p className="text-[11px] text-slate-400 truncate">{lead.company || lead.email}</p>
      </div>
      {lead.owner_name && (
        <span title={`Responsabile: ${lead.owner_name}`} className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-300 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
          {initials(lead.owner_name)}
        </span>
      )}
    </div>
    <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
      {lead.prospect_type && <span className="px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 uppercase tracking-wide">{PROSPECT_TYPES.find((p) => p.id === lead.prospect_type)?.label}</span>}
      {lead.englishLevel && <span className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-200">{lead.englishLevel}</span>}
      {Number(lead.estimated_value) > 0 && <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">{money(lead.estimated_value)}</span>}
    </div>
    <ActionBadges done={lead.actions_done} testIdPrefix={`lead-${lead.id}`} />
    <NextTaskChip lead={lead} />
  </div>
);
